import type { CSSProperties } from 'react';
import { hexToHsl } from './color';

/**
 * Dashboard and panel color overrides.
 *
 * There is no stylesheet generation here and no scoped CSS to parse. Every
 * tier writes the *same* CSS variable names, each onto a narrower element, and
 * the cascade resolves them:
 *
 *   :root            tenant whitelabeling   (useWhitelabelOverrides)
 *   .pl-dashboard    this dashboard         (Dashboard.theme)
 *   .pl-panel        one panel              (DashboardComponent.theme)
 *
 * A field left unset simply is not written, so it inherits from the tier above
 * rather than resetting to the product default. That is why the UI has to
 * distinguish "unset" from "set to the default color" — clearing a field hands
 * control back up the chain.
 */

/**
 * How a variable wants its value written.
 *
 * The two conventions already in index.css: the shadcn tokens hold a bare
 * "H S% L%" triplet and are consumed through hsl(), while the chart tokens hold
 * a complete color and are consumed directly.
 */
export type ThemeVarFormat = 'hsl' | 'hex';

export interface ThemeVar {
  name: string;
  format: ThemeVarFormat;
}

export interface DashboardThemeField {
  key: string;
  label: string;
  /** What the color paints, for the UI's own description. */
  hint: string;
  vars: ThemeVar[];
  /** A panel does not own the page it sits on, so some fields are top-level only. */
  scope: 'dashboard' | 'both';
  /**
   * Which element carries the variable.
   *
   * 'content' stops at the dashboard's canvas, leaving the top bar and the
   * filter bar on the app's own colors — those are Pliable's chrome, not the
   * dashboard's surface, and a dashboard that repainted them would leave its
   * own controls sitting on a background chosen for panels.
   *
   * 'root' covers the whole page, for a color that is the brand rather than
   * the surface: the primary color has to reach the Refresh button in the
   * filter bar to mean anything.
   */
  target: 'root' | 'content';
}

export const DASHBOARD_THEME_FIELDS: DashboardThemeField[] = [
  {
    key: 'primary_color',
    label: 'Primary',
    hint: 'Buttons and other accents — Refresh, Apply, Save.',
    scope: 'dashboard',
    // The one color that belongs on the whole page rather than the canvas:
    // the Refresh button lives in the filter bar, above where `content` starts.
    target: 'root',
    vars: [{ name: '--primary', format: 'hsl' }],
  },
  {
    key: 'page_bg_color',
    label: 'Dashboard background',
    hint: 'The page behind the panels. The bar above it keeps the app\'s colors.',
    scope: 'dashboard',
    target: 'content',
    vars: [{ name: '--page-bg', format: 'hsl' }],
  },
  {
    key: 'card_bg_color',
    target: 'content',
    label: 'Panel background',
    hint: 'The surface each panel is drawn on.',
    scope: 'both',
    vars: [{ name: '--card', format: 'hsl' }],
  },
  {
    key: 'text_color',
    target: 'content',
    label: 'Text',
    hint: 'Titles, labels and values. Muted text is derived from it.',
    scope: 'both',
    // --muted-foreground is derived rather than exposed: a custom text color
    // against the default muted gray is the combination that actually goes
    // wrong (white text keeps dark descriptions and axis labels), and nobody
    // wants to pick the second color by hand to avoid it.
    vars: [
      { name: '--foreground', format: 'hsl' },
      { name: '--card-foreground', format: 'hsl' },
    ],
  },
  {
    key: 'border_color',
    target: 'content',
    label: 'Border',
    hint: 'Panel outlines and the dividers drawn with them.',
    scope: 'both',
    vars: [{ name: '--border', format: 'hsl' }],
  },
  {
    key: 'chart_1_color',
    target: 'content',
    label: 'Series 1',
    hint: 'First bar, line or slice.',
    scope: 'both',
    vars: [{ name: '--chart-1', format: 'hex' }],
  },
  {
    key: 'chart_2_color',
    target: 'content',
    label: 'Series 2',
    hint: 'Second bar, line or slice.',
    scope: 'both',
    vars: [{ name: '--chart-2', format: 'hex' }],
  },
  {
    key: 'chart_3_color',
    target: 'content',
    label: 'Series 3',
    hint: 'Third bar, line or slice.',
    scope: 'both',
    vars: [{ name: '--chart-3', format: 'hex' }],
  },
  {
    key: 'chart_4_color',
    target: 'content',
    label: 'Series 4',
    hint: 'Fourth bar, line or slice.',
    scope: 'both',
    vars: [{ name: '--chart-4', format: 'hex' }],
  },
  {
    key: 'chart_5_color',
    target: 'content',
    label: 'Series 5',
    hint: 'Fifth bar, line or slice.',
    scope: 'both',
    vars: [{ name: '--chart-5', format: 'hex' }],
  },
];

export type DashboardTheme = Partial<Record<string, string | null | undefined>>;

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** Expand #abc so hexToHsl, which indexes fixed offsets, reads it correctly. */
function normalizeHex(hex: string): string {
  if (hex.length !== 4) return hex;
  const [, r, g, b] = hex;
  return `#${r}${r}${g}${g}${b}${b}`;
}

/**
 * Pull a lightness toward the middle to stand in for muted text.
 *
 * Toward, not away: it has to work from both ends, dimming black text and
 * dimming white text, and the midpoint is the only direction that does both.
 */
function mutedFromHsl(triplet: string): string | null {
  const match = triplet.match(/^(\d+) (\d+)% (\d+)%$/);
  if (!match) return null;
  const [, h, s, l] = match;
  const lightness = Number(l);
  return `${h} ${s}% ${Math.round(lightness + (50 - lightness) * 0.45)}%`;
}

/**
 * Turn a theme into the inline CSS variables that express it.
 *
 * Returns undefined when nothing is set so callers can leave `style` off the
 * element entirely rather than stamping an empty object onto every panel.
 */
export function themeToCssVars(
  theme: DashboardTheme | null | undefined,
  scope: 'dashboard' | 'component' = 'dashboard',
  target: 'root' | 'content' | 'all' = 'all',
): CSSProperties | undefined {
  if (!theme) return undefined;

  const style: Record<string, string> = {};

  for (const field of DASHBOARD_THEME_FIELDS) {
    if (scope === 'component' && field.scope === 'dashboard') continue;
    // A panel sits inside the canvas already, so every field that reaches it
    // reaches it the same way — the split only matters at dashboard level.
    if (target !== 'all' && scope === 'dashboard' && field.target !== target) continue;

    const raw = theme[field.key];
    if (!raw || typeof raw !== 'string') continue;
    const value = raw.trim();
    if (!value) continue;

    // hexToHsl only knows hex. A value in any other notation still works for a
    // variable consumed directly, so pass those through and skip the ones that
    // would land inside hsl() and break the rule they are part of.
    const isHex = HEX_RE.test(value);
    const hex = isHex ? normalizeHex(value) : null;

    for (const { name, format } of field.vars) {
      if (format === 'hex') {
        style[name] = value;
      } else if (hex) {
        style[name] = hexToHsl(hex);
      }
    }

    if (field.key === 'text_color' && hex) {
      const muted = mutedFromHsl(hexToHsl(hex));
      if (muted) style['--muted-foreground'] = muted;
    }
  }

  return Object.keys(style).length > 0 ? (style as CSSProperties) : undefined;
}

/** True when a theme actually sets something — used to skip empty writes. */
export function hasThemeOverrides(theme: DashboardTheme | null | undefined): boolean {
  if (!theme) return false;
  return DASHBOARD_THEME_FIELDS.some((f) => {
    const v = theme[f.key];
    return typeof v === 'string' && v.trim() !== '';
  });
}

/**
 * The product defaults, as hex, for the swatch shown when nothing overrides a
 * field. These mirror the :root values in index.css — they are for display
 * only, so a drift here changes what the picker previews, never what renders.
 */
export const THEME_FIELD_DEFAULTS: Record<string, string> = {
  primary_color: '#5a3ed8',
  page_bg_color: '#fcfcfd',
  card_bg_color: '#ffffff',
  text_color: '#0a0f1a',
  border_color: '#e2e8f0',
  chart_1_color: '#e05a2b',
  chart_2_color: '#2a9d8f',
  chart_3_color: '#3d5a80',
  chart_4_color: '#e9c46a',
  chart_5_color: '#d4a843',
};

/**
 * Flatten the tiers above a given scope into "what you would see here if you
 * set nothing", for the editor's swatches. Later arguments win, so pass them
 * outermost-first: whitelabeling, then the dashboard, then the component.
 */
export function resolveInheritedColors(
  ...tiers: (DashboardTheme | Record<string, any> | null | undefined)[]
): Record<string, string> {
  const resolved: Record<string, string> = { ...THEME_FIELD_DEFAULTS };
  for (const tier of tiers) {
    if (!tier) continue;
    for (const field of DASHBOARD_THEME_FIELDS) {
      const value = tier[field.key];
      if (typeof value === 'string' && value.trim()) resolved[field.key] = value.trim();
    }
  }
  return resolved;
}
